import * as THREE from "three";
import type { ThemeColors } from "./Scene";
import { LANE_WIDTH } from "@shared/constants";
import type { Path } from "./Path";
import { addThemeDecor } from "./decor";

const SEGMENT_LENGTH = 6; // shorter so curves bend smoothly
const PATH_WIDTH = LANE_WIDTH * 5 + 1.6;

/**
 * The world bends along a curved Path. Stone tiles, parapet walls, and
 * theme terrain all follow the curve. Spectators cheer at the finish, and
 * a per-theme decoration layer (snow, birds, bears) brings the surroundings
 * to life.
 */
export class World {
  group = new THREE.Group();
  private courseLength: number;
  private spectators: { mesh: THREE.Object3D; phase: number; baseY: number }[] = [];
  private decorTick: ((now: number) => void) | null = null;

  constructor(theme: ThemeColors, courseLength: number, path: Path) {
    this.courseLength = courseLength;

    // Soft cloud shelf far below the course so falling reads as dropping out
    // of a sky arena instead of clipping through terrain.
    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(800, 800),
      new THREE.MeshBasicMaterial({
        map: makeCloudFloorTexture(),
        transparent: true,
        opacity: 0.62,
        depthWrite: false,
      }),
    );
    ground.rotation.x = -Math.PI / 2;
    ground.position.set(0, -18, courseLength / 2);
    this.group.add(ground);

    this.buildPathTiles(theme, path, courseLength);
    this.buildStartGuard(theme, path);

    // Finish line + grandstands at end-of-path location.
    const finishS = path.length;
    const finishPos = path.positionAt(finishS, 0);
    const finishYaw = path.yawAt(finishS);
    const finish = this.buildFinish(theme);
    finish.position.copy(finishPos);
    finish.rotation.y = finishYaw;
    this.group.add(finish);
    this.buildGrandstand(path);

    // Theme decorations (snow particles, jungle birds, etc.).
    this.decorTick = addThemeDecor(this.group, path, theme, courseLength);
  }

  /** Per-frame: cheering spectators + theme decor (snow falls, birds flap). */
  update(t: number) {
    for (const s of this.spectators) {
      s.mesh.position.y = s.baseY + Math.abs(Math.sin(t * 0.006 + s.phase)) * 0.45;
    }
    this.decorTick?.(t);
  }

  private buildPathTiles(theme: ThemeColors, path: Path, courseLength: number) {
    const lightMat = new THREE.MeshStandardMaterial({ color: theme.pathLight });
    const darkMat = new THREE.MeshStandardMaterial({ color: theme.pathDark });
    const wallMat = new THREE.MeshStandardMaterial({ color: theme.pathDark });
    const wallGeo = new THREE.BoxGeometry(0.6, 1.0, SEGMENT_LENGTH * 1.05);

    let alt = false;
    for (let s = 0; s < courseLength + SEGMENT_LENGTH; s += SEGMENT_LENGTH) {
      const center = Math.min(courseLength, s + SEGMENT_LENGTH / 2);
      const pos = path.positionAt(center, 0);
      const yaw = path.yawAt(center);

      const narrowHalfWidth = path.narrowBridgeHalfWidthAt(center);
      const pathWidth = narrowHalfWidth ? narrowHalfWidth * 2 : PATH_WIDTH;
      const tile = new THREE.Mesh(
        new THREE.BoxGeometry(pathWidth, 0.4, SEGMENT_LENGTH * 1.05),
        alt ? lightMat : darkMat,
      );
      tile.position.copy(pos);
      tile.position.y = pos.y - 0.2;
      tile.rotation.y = yaw;
      tile.rotation.x = -Math.atan(path.heightSlopeAt(center));
      tile.receiveShadow = true;
      this.group.add(tile);

      // Side parapets — omitted on the narrow swinging-log bridge.
      if (!narrowHalfWidth) {
        for (const sign of [-1, 1] as const) {
          const wall = new THREE.Mesh(wallGeo, wallMat);
          const off = path.positionAt(center, sign * (PATH_WIDTH / 2 + 0.3));
          wall.position.copy(off);
          wall.position.y = off.y + 0.3;
          wall.rotation.y = yaw;
          wall.rotation.x = -Math.atan(path.heightSlopeAt(center));
          wall.castShadow = true;
          wall.receiveShadow = true;
          this.group.add(wall);
        }
      }
      const hNow = path.heightAt(center);
      const hNext = path.heightAt(Math.min(courseLength, center + SEGMENT_LENGTH));
      if (hNext - hNow > 0.85) {
        const face = new THREE.Mesh(
          new THREE.BoxGeometry(pathWidth + 0.2, hNext - hNow, 0.45),
          wallMat,
        );
        const fpos = path.positionAt(center + SEGMENT_LENGTH / 2, 0);
        face.position.copy(fpos);
        face.position.y = hNow + (hNext - hNow) / 2 - 0.02;
        face.rotation.y = yaw;
        face.castShadow = true;
        face.receiveShadow = true;
        this.group.add(face);
      }
      alt = !alt;
    }
  }

  private buildStartGuard(theme: ThemeColors, path: Path) {
    const mat = new THREE.MeshStandardMaterial({ color: theme.pathDark, roughness: 0.62 });
    const start = path.positionAt(0, 0);
    const tan = path.tangentAt(0);
    const yaw = path.yawAt(0);
    const guard = new THREE.Group();
    guard.position.set(start.x - tan.x * 0.85, start.y, start.z - tan.z * 0.85);
    guard.rotation.y = yaw;

    const rail = new THREE.Mesh(new THREE.BoxGeometry(PATH_WIDTH + 1.6, 0.9, 0.55), mat);
    rail.position.y = 0.55;
    rail.castShadow = true;
    rail.receiveShadow = true;
    guard.add(rail);

    const capMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.55 });
    const cap = new THREE.Mesh(new THREE.BoxGeometry(PATH_WIDTH + 1.9, 0.16, 0.62), capMat);
    cap.position.y = 1.08;
    cap.castShadow = true;
    cap.receiveShadow = true;
    guard.add(cap);

    this.group.add(guard);
  }

  /**
   * Build the three grandstands at the finish line. All positions come from
   * the Path so they always sit perpendicular to the path tangent at the
   * finish — even when the level ends inside a corner.
   *
   *   side stands: ±11m perpendicular, 4m back from the finish line
   *   back stand: 6m past the finish, facing back at the player
   */
  private buildGrandstand(path: Path) {
    const stadiumColors = [0xef476f, 0xffd166, 0x06d6a0, 0x6ec5ff, 0x9b5cf6, 0xff8b3d];
    const seatMat = new THREE.MeshStandardMaterial({ color: 0x4a4a5a });

    const finishS = path.length;
    const yaw = path.yawAt(finishS);
    const finishPos = path.positionAt(finishS, 0);
    const tangent = path.tangentAt(finishS);

    // Side stands — placed using path.positionAt so the perpendicular
    // direction always matches the in-game lane offset convention.
    for (const side of [-1, 1] as const) {
      const center = path.positionAt(Math.max(0, finishS - 4), side * 11);
      const stand = new THREE.Group();
      stand.position.set(center.x, 0, center.z);
      stand.rotation.y = yaw;
      for (let tier = 0; tier < 3; tier++) {
        const tierMesh = new THREE.Mesh(
          new THREE.BoxGeometry(5, 1.2 + tier * 0.4, 14),
          seatMat,
        );
        tierMesh.position.set(side * (tier * 1.6), 0.6 + tier * 1.2, 0);
        tierMesh.castShadow = true;
        tierMesh.receiveShadow = true;
        stand.add(tierMesh);
        for (let i = 0; i < 7; i++) {
          const spec = new THREE.Mesh(
            new THREE.SphereGeometry(0.42, 12, 10),
            new THREE.MeshStandardMaterial({
              color: stadiumColors[(tier * 7 + i) % stadiumColors.length]!,
            }),
          );
          spec.castShadow = true;
          const baseY = 1.2 + tier * 1.2 + 0.62;
          spec.position.set(side * (tier * 1.6), baseY, -6 + i * 2);
          stand.add(spec);
          this.spectators.push({ mesh: spec, phase: tier * 1.3 + i * 0.7, baseY });
        }
      }
      this.group.add(stand);
    }

    // Back stand — extends straight off the end of the path along the finish
    // tangent. positionAt clamps at length, so we step manually past the end.
    const back = new THREE.Group();
    back.position.set(
      finishPos.x + tangent.x * 6,
      0,
      finishPos.z + tangent.z * 6,
    );
    back.rotation.y = yaw + Math.PI; // facing back at the runner
    for (let tier = 0; tier < 3; tier++) {
      const tierMesh = new THREE.Mesh(
        new THREE.BoxGeometry(20, 1.2 + tier * 0.4, 4),
        seatMat,
      );
      tierMesh.position.set(0, 0.6 + tier * 1.2, tier * 1.6);
      tierMesh.castShadow = true;
      tierMesh.receiveShadow = true;
      back.add(tierMesh);
      for (let i = 0; i < 9; i++) {
        const spec = new THREE.Mesh(
          new THREE.SphereGeometry(0.42, 12, 10),
          new THREE.MeshStandardMaterial({
            color: stadiumColors[(tier * 9 + i) % stadiumColors.length]!,
          }),
        );
        spec.castShadow = true;
        const baseY = 1.2 + tier * 1.2 + 0.62;
        spec.position.set(-8 + i * 2, baseY, tier * 1.6);
        back.add(spec);
        this.spectators.push({ mesh: spec, phase: tier * 1.1 + i * 0.5 + 2.4, baseY });
      }
    }
    this.group.add(back);
  }

  private buildFinish(theme: ThemeColors): THREE.Group {
    const g = new THREE.Group();
    void theme;
    const postMat = new THREE.MeshStandardMaterial({ color: 0xeeeeee });
    const postGeo = new THREE.BoxGeometry(0.5, 6, 0.5);
    for (const x of [-PATH_WIDTH / 2, PATH_WIDTH / 2]) {
      const post = new THREE.Mesh(postGeo, postMat);
      post.position.set(x, 3, 0);
      post.castShadow = true;
      g.add(post);
    }
    const banner = new THREE.Mesh(
      new THREE.BoxGeometry(PATH_WIDTH + 1, 1.2, 0.3),
      new THREE.MeshStandardMaterial({ color: 0xffd166 }),
    );
    banner.position.set(0, 5.5, 0);
    g.add(banner);
    return g;
  }

  get length() {
    return this.courseLength;
  }
}

let cloudFloorTexture: THREE.CanvasTexture | null = null;
function makeCloudFloorTexture() {
  if (cloudFloorTexture) return cloudFloorTexture;
  const canvas = document.createElement("canvas");
  canvas.width = 1024;
  canvas.height = 1024;
  const ctx = canvas.getContext("2d")!;
  const grad = ctx.createLinearGradient(0, 0, 0, canvas.height);
  grad.addColorStop(0, "#95d9ff");
  grad.addColorStop(1, "#dff7ff");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  for (let i = 0; i < 90; i++) {
    const x = (i * 137.7) % canvas.width;
    const y = (i * 71.3) % canvas.height;
    const r = 38 + (i % 9) * 14;
    const puff = ctx.createRadialGradient(x, y, 0, x, y, r);
    puff.addColorStop(0, "rgba(255,255,255,0.9)");
    puff.addColorStop(0.58, "rgba(255,255,255,0.52)");
    puff.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = puff;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  cloudFloorTexture = new THREE.CanvasTexture(canvas);
  cloudFloorTexture.colorSpace = THREE.SRGBColorSpace;
  cloudFloorTexture.wrapS = THREE.RepeatWrapping;
  cloudFloorTexture.wrapT = THREE.RepeatWrapping;
  cloudFloorTexture.repeat.set(4, 4);
  return cloudFloorTexture;
}
